import { IDapiCacheRequest, xhrFetch } from '@origin-digital/daxi';
import * as paymentServiceApis from '@origin-digital/payment-service-dapi-contract';
import * as paymentMethodApis from '@origin-digital/pmservice-dapi-contract';
import * as billPayApis from '@origin-digital/billpay-dapi-contract';
import * as dashboardApis from '@origin-digital/dashboard-dapi-contract';

import { IKrakenPayData, IKrakenPayResponse } from 'src/interfaces';
import { apiBasePaths, apiPaths } from 'src/consts';
import { config } from 'src/config';
import { isMyAccount } from 'src/utils/helpers';

export const daxiConfig: IDapiCacheRequest = {
  authorization: 'raw',
  ttlInMin: 30,
};

type IApi<D, R> = (
  baseUrl: string,
  data: D,
  authorization?: IDapiCacheRequest['authorization'],
  ttlInMin?: IDapiCacheRequest['ttlInMin'],
) => Promise<R>;
type IApiWithoutData<R> = (
  baseUrl: string,
  authorization?: IDapiCacheRequest['authorization'],
  ttlInMin?: IDapiCacheRequest['ttlInMin'],
) => Promise<R>;

const callApi = <D, R>(
  basePath: string,
  api: IApi<D, R>,
  authorization: IDapiCacheRequest['authorization'] = daxiConfig.authorization,
) => (data: D) => api(basePath, data, authorization, daxiConfig.ttlInMin);
const callUnauthApi = <D, R>(basePath: string, api: IApi<D, R>) => (data: D) => api(basePath, data);
const callApiWithoutData = <R>(basePath: string, api: IApiWithoutData<R>) => () =>
  api(basePath, daxiConfig.authorization, daxiConfig.ttlInMin);

const callBillpayApi = <D, R>(api: IApi<D, R>) => callUnauthApi(apiBasePaths.billingPayment, api);
const callDasboardApi = <D, R>(api: IApi<D, R>) => callApi(apiBasePaths.dashboard, api, 'jwt');
export const callPaymentApi = <D, R>(api: IApi<D, R>, isUnauth?: boolean) =>
  !isUnauth ? callApi(apiBasePaths.payment, api) : callUnauthApi(apiBasePaths.payment, api);
const callPaymentMethodApi = <R>(api: IApiWithoutData<R>) =>
  callApiWithoutData(apiBasePaths.paymentMethods, api);

// Kraken APIs
export const krakenPay = async (
  data: IKrakenPayData,
  accountNumber: string,
): Promise<IKrakenPayResponse> => {
  const path = apiPaths.krakenPayment
    .replace(':krakenUrl', config.krakenBaseUrl)
    .replace(':accountNumber', accountNumber);

  const result = await xhrFetch<IKrakenPayResponse>({
    path,
    method: 'POST',
    body: data as any,
  });

  return result.data;
};

// Billing Payments APIs
export const getInvoiceInfo = callBillpayApi(billPayApis.getInvoiceInfo);

// Dashboard APIs
export const getLPGCustomerData = callDasboardApi(dashboardApis.getLPGCustomerData);

// Payment Service APIs
export const emailReceiptsPost = callPaymentApi(
  paymentServiceApis.emailReceiptsPost,
  !isMyAccount(),
);
export const getAccountBalances = callPaymentApi(paymentServiceApis.getAccountBalances);
export const savedCardPay = callPaymentApi(paymentServiceApis.savedCardPay);
export const verifyContractAccount = callPaymentApi(paymentServiceApis.verifyContractAccount, true);
export const authPay = callPaymentApi(paymentServiceApis.authPay);
export const adhocPay = callPaymentApi(paymentServiceApis.adhocPay, true);

// Payment Method APIs
export const getCards = callPaymentMethodApi(paymentMethodApis.getCards);
